import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListingSortOption, mapSearchListingResults } from '@pkgs/api';

import { defaultSettingsByLang } from '@/utils/settings';
import { PageVariant } from '@/types/expose';
import { useExposePageProps } from '@/hooks/useExposePageProps';

import { useListing } from './useListing';
import { getSimilarListingsQueryKey } from './queryKeys';
import { useSettings } from '../useSettings';
import { useSearchApi } from '../useSearchApi';
import { useExposeLanguageStore } from '../useExposeLanguageStore';

export const useSimilarListings = () => {
  const searchApi = useSearchApi();
  const queryClient = useQueryClient();
  const { listingId, pathLanguage, pageVariant, promotedBy } = useExposePageProps();
  const { currency, measurementSystem, language } = useSettings();
  const {
    data: { genericListing, listing },
  } = useListing();

  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage ?? language);
  const isPrivateOffice = genericListing.isPrivateOffice && genericListing.agent && pageVariant !== PageVariant.EXPOSE;
  const query = useQuery({
    queryKey: getSimilarListingsQueryKey({
      listingId,
      language: exposeLanguage,
      currency,
      measurementSystem,
      agentId: isPrivateOffice ? promotedBy || genericListing.agent?.id : undefined,
    }),
    queryFn: async () => {
      let similarPropertiesResult;
      if (isPrivateOffice) {
        similarPropertiesResult = await searchApi.searchListings(
          {
            agentId: promotedBy || genericListing.agent?.id,
            propertyMarketingType: 'propertyMarketingType' in genericListing ? [genericListing.propertyMarketingType] : [],
            businessArea: listing ? [listing.businessArea] : [],
            sortingOptions: [ListingSortOption.SALES_PRICE_DESC],
          },
          {
            language: exposeLanguage,
            pageSize: 12,
            currency,
            measurementSystem,
          },
        );
      } else {
        if (!listing) return;
        similarPropertiesResult = await searchApi.getSimilarListings(listing, {
          language: exposeLanguage,
          currency,
          measurementSystem,
        });
      }

      if (similarPropertiesResult.status === 'error') {
        throw similarPropertiesResult.error;
      }

      return mapSearchListingResults(similarPropertiesResult.data.listings);
    },
    refetchOnMount: false,

    placeholderData: (previousData) => {
      if (previousData) return previousData;

      // Try to get data from server query key if client settings differ
      const serverQueryKey = getSimilarListingsQueryKey({
        listingId,
        language: pathLanguage,
        ...defaultSettingsByLang[pathLanguage],
        agentId: promotedBy === '' ? genericListing.agent?.id : (promotedBy ?? undefined),
      });
      return queryClient.getQueryData(serverQueryKey);
    },
    enabled: !listing?.isDevelopmentProject && !listing?.isDevelopmentUnit,
  });

  return query;
};
